// geoFix — the app's ONE reading of the GPS fix a crew phone recorded.
//
// The coordinates are collected at every clock in and out (apps/field/geo.js →
// routes/field.js) and the owner's complaint about them is on the record:
// "admins don't see the location collected on the jobs."
// docs/PARITY_2026-09-08.md §7. Two screens answer it now — the Office job
// panel (office/records/Jobs.js) and the live board (dispatch/screens/Now.js)
// — and they are in different SECTIONS, so this cannot live in either of them:
// sectionsDoNotCrossImport.test.js refuses `dispatch` importing from `office`,
// and it is right to. A thing two sections need is an app thing.
//
// ── Why one reader has to handle two spellings ──────────────────────────────
//
// The server answers with the SAME two numbers under two different names,
// because two routes shape the same column pair differently:
//
//   latitude / longitude   the time_entries row itself — GET /api/jobs/:id/entries
//                          (server/routes/jobs.js:132), Reports, the CSV
//   lat / lng              aliased in SQL for the live board —
//                          `te.latitude AS lat, te.longitude AS lng`
//                          (server/routes/dispatch.js clocked_in)
//
// A formatter that knows only one of those returns '' for half the app's
// rows — silently, and "no location recorded" is a sentence a dispatcher
// believes. So the normalisation happens once, here, and every caller asks the
// same question.
//
// ── Number.isFinite, not truthiness ─────────────────────────────────────────
//
// Carried from the classic screen's bug and kept: `entry.latitude && …` drops
// a 0 coordinate, and 0 is a real place. A value also survives JSON as
// whatever was stored, so `.toFixed` on a string throws INSIDE a render, which
// takes the page down rather than one cell.
//
// ── What this deliberately does NOT do ──────────────────────────────────────
//
// It does not say how far the fix is from the property. Nothing in the schema
// can: `properties` has address/city/state and no coordinate columns
// (server/database.js CREATE TABLE properties), and time_entries.latitude is
// the only lat/lng pair in the database. Answering "was the crew AT the
// property" in metres needs a property coordinate, which is a migration, which
// ships alone and with a human (CLAUDE.md rule 3).

/**
 * The fix on an entry, normalised, or null when there isn't one.
 * @returns {{lat: number, lng: number}|null}
 */
export function gpsFix(entry) {
  if (!entry) return null;
  const present = (v) => v !== null && v !== undefined && v !== '';
  const rawLat = present(entry.latitude) ? entry.latitude : entry.lat;
  const rawLng = present(entry.longitude) ? entry.longitude : entry.lng;
  if (!present(rawLat) || !present(rawLng)) return null;
  const lat = Number(rawLat);
  const lng = Number(rawLng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

/**
 * "41.7600, -72.6800" for an entry that carries a fix, '' for one that does
 * not. Four decimal places is about 11 metres — enough to say which property,
 * short enough to sit beside a link on a 320px screen.
 */
export function gpsText(entry) {
  const fix = gpsFix(entry);
  if (!fix) return '';
  return `${fix.lat.toFixed(4)}, ${fix.lng.toFixed(4)}`;
}

/**
 * How wrong the fix might be, in metres — or null when nothing says.
 *
 * ── Read this before trusting an absent answer ──────────────────────────────
 *
 * `time_entries` HAS NO ACCURACY COLUMN today (server/database.js CREATE TABLE
 * time_entries: latitude, longitude, timezone, and no third). So on every row
 * in the database right now this returns null, and every screen that draws a
 * fix must be legible with no figure at all.
 *
 * It is written anyway, and read defensively, because the capture side is
 * being added separately and a fix WITHOUT an accuracy is the dangerous one:
 * a phone that answers from a cached cell-tower position is routinely a mile
 * out, and four decimal places of latitude renders that mile as a confident
 * eleven-metre reading. The number is the difference between "the crew is at
 * the property" and "the crew's phone last knew where it was near the depot".
 *
 * Two spellings accepted for the same reason gpsFix accepts two: whichever
 * name the capture lands on, one reader answers. `accuracy` is what the
 * browser's own GeolocationCoordinates calls it and is the likelier column;
 * `accuracy_m` is the likelier SQL alias. A negative or zero radius is not a
 * measurement and is refused.
 */
export function gpsAccuracy(entry) {
  if (!entry) return null;
  const raw = entry.accuracy !== null && entry.accuracy !== undefined && entry.accuracy !== ''
    ? entry.accuracy
    : entry.accuracy_m;
  if (raw === null || raw === undefined || raw === '') return null;
  const m = Number(raw);
  if (!Number.isFinite(m) || m <= 0) return null;
  return m;
}

/**
 * "±25 m" / "±1.2 km", or '' when the row carries no accuracy.
 *
 * Rounded hard on purpose. A GPS radius is itself an estimate, so "±23.4 m"
 * claims a precision about an imprecision, and the only decision it feeds is
 * "can I believe this pin" — which needs an order of magnitude, not a decimal.
 */
export function accuracyText(entry) {
  const m = gpsAccuracy(entry);
  if (m === null) return '';
  if (m >= 1000) return `±${Math.round(m / 100) / 10} km`;
  return `±${Math.round(m)} m`;
}

/**
 * The maps URL for one fix, or '' when there is none.
 *
 * ONE spelling of this link. It was written twice already — Now.js's sheet and
 * Jobs.js's `mapHref` — and the second copy carries a comment saying it was
 * copied "character for character, because shipping a second spelling of the
 * same link would mean two behaviours to keep working". This is that comment,
 * enforced.
 */
export function mapHref(entry) {
  const fix = gpsFix(entry);
  if (!fix) return '';
  return `https://www.google.com/maps?q=${fix.lat},${fix.lng}`;
}
